document.addEventListener('DOMContentLoaded', () => {
  const nav = document.querySelector('[data-nav]');
  const toggle = document.querySelector('[data-menu-toggle]');
  toggle?.addEventListener('click', () => {
    const open = nav.classList.toggle('open');
    toggle.setAttribute('aria-expanded', String(open));
  });
  nav?.querySelectorAll('a').forEach(link => {
    if (new URL(link.href).pathname === location.pathname) link.classList.add('active');
  });

  document.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
    document.querySelectorAll('[data-filter]').forEach(item => item.classList.remove('active'));
    button.classList.add('active');
    const filter = button.dataset.filter;
    document.querySelectorAll('[data-product-card]').forEach(card => {
      card.hidden = filter !== 'all' && card.dataset.category !== filter;
    });
  }));

  const visualizer = document.querySelector('[data-ai-visualizer]');
  if (visualizer) {
    const preview = visualizer.querySelector('[data-preview]');
    const overlay = visualizer.querySelector('[data-overlay]');
    const photo = visualizer.querySelector('[data-wall-photo]');
    const aiResult = visualizer.querySelector('[data-ai-result]');
    const aiStatus = visualizer.querySelector('[data-ai-status]');
    const previewNote = visualizer.querySelector('[data-preview-note]');
    const requestButton = visualizer.querySelector('[data-generate]');
    const aiButton = visualizer.querySelector('[data-ai-generate]');
    const upload = visualizer.querySelector('[data-wall-upload]');
    const uploadTitle = visualizer.querySelector('[data-upload-title]');
    const endpoint = visualizer.dataset.aiEndpoint || '/apps/cnc-visualizer/generate';
    let wallImageData = '';
    let generationController;
    let resultType = '';

    const selectedPattern = () => visualizer.querySelector('[data-pattern].selected');
    const selectedMaterial = () => visualizer.querySelector('[data-material].selected');

    const updateRequestLink = () => {
      if (!requestButton) return;
      const pattern = selectedPattern();
      const material = selectedMaterial();
      const params = new URLSearchParams({
        pattern: pattern?.dataset.code || 'PR001',
        design: pattern?.dataset.name || '',
        material: material?.dataset.material || 'MDF / HMR'
      });
      if (wallImageData) params.set('wall', 'uploaded');
      if (resultType) params.set('preview', resultType);
      requestButton.href = `/pages/contact-us?${params.toString()}`;
      requestButton.setAttribute('aria-disabled', String(!wallImageData));
    };

    const setBusy = busy => {
      preview?.classList.toggle('is-processing', busy);
      if (aiStatus) aiStatus.hidden = !busy;
      if (aiButton) {
        aiButton.disabled = busy || !wallImageData;
        aiButton.textContent = busy ? 'Generating…' : 'Regenerate AI preview';
      }
    };

    const showAIError = error => {
      resultType = '';
      preview?.classList.remove('ai-generated', 'ai-ready');
      preview?.classList.add('ai-failed');
      const status = Number(error?.status || 0);
      const code = String(error?.code || 'AI_REQUEST_FAILED');
      let message = 'AI preview could not be generated. Please try again.';
      if (status === 404 || code === 'NON_JSON_RESPONSE') {
        message = 'AI connection is not configured. Check that the Shopify app is installed and the App Proxy path is /apps/cnc-visualizer.';
      } else if (status === 401) {
        message = 'Shopify rejected the App Proxy signature. Check SHOPIFY_API_SECRET in Vercel.';
      } else if (status === 403) {
        message = 'This Shopify store is not allowed. Check ALLOWED_SHOP in Vercel.';
      } else if (status === 503) {
        message = 'The AI service is missing a required environment variable in Vercel.';
      } else if (status === 502) {
        message = 'OpenAI could not generate this preview. Check the Vercel runtime logs and your OpenAI API balance.';
      }
      if (previewNote) previewNote.textContent = `${message} (${status || code})`;
      updateRequestLink();
    };

    const resizeWallPhoto = file => new Promise((resolve, reject) => {
      const sourceUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        const maxSide = 1600;
        const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d', { alpha: false }).drawImage(image, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(sourceUrl);
        resolve(canvas.toDataURL('image/jpeg', 0.86));
      };
      image.onerror = () => {
        URL.revokeObjectURL(sourceUrl);
        reject(new Error('IMAGE_DECODE_FAILED'));
      };
      image.src = sourceUrl;
    });

    const generateAI = async () => {
      if (!wallImageData) return;
      generationController?.abort();
      generationController = new AbortController();
      const pattern = selectedPattern();
      const material = selectedMaterial();
      resultType = '';
      preview?.classList.remove('ai-generated', 'ai-ready', 'ai-failed');
      setBusy(true);
      if (previewNote) previewNote.textContent = 'AI is analysing the wall and preparing your CNC concept…';
      updateRequestLink();

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            wall_image: wallImageData,
            pattern: pattern?.dataset.code || 'PR001',
            pattern_name: pattern?.dataset.name || '',
            pattern_image_url: pattern?.dataset.image || overlay?.src || '',
            material: material?.dataset.material || 'MDF / HMR'
          }),
          signal: generationController.signal
        });
        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json') ? await response.json().catch(() => ({})) : {};
        if (!response.ok || !payload.image) {
          const requestError = new Error(payload.error || (contentType.includes('application/json') ? 'AI_REQUEST_FAILED' : 'NON_JSON_RESPONSE'));
          requestError.status = response.status;
          requestError.code = contentType.includes('application/json') ? payload.error : 'NON_JSON_RESPONSE';
          throw requestError;
        }
        if (aiResult) aiResult.src = payload.image;
        resultType = payload.provider || 'ai';
        preview?.classList.add('ai-generated', 'ai-ready');
        if (previewNote) previewNote.textContent = `${payload.provider === 'gemini' ? 'Gemini' : payload.provider === 'openai' ? 'OpenAI' : 'AI'} concept preview. Final proportions, materials and colours are confirmed after measurement.`;
        updateRequestLink();
      } catch (error) {
        if (error.name !== 'AbortError') showAIError(error);
      } finally {
        setBusy(false);
      }
    };

    const markSelectionChanged = () => {
      if (!wallImageData) return;
      resultType = '';
      preview?.classList.remove('ai-generated', 'ai-ready', 'ai-failed');
      if (previewNote) previewNote.textContent = 'Selection changed. Generate a new AI preview to update the result.';
      if (aiButton) aiButton.disabled = false;
      updateRequestLink();
    };

    visualizer.querySelectorAll('[data-pattern]').forEach(button => button.addEventListener('click', () => {
      visualizer.querySelectorAll('[data-pattern]').forEach(item => item.classList.remove('selected'));
      button.classList.add('selected');
      if (overlay) overlay.src = button.dataset.image;
      const code = visualizer.querySelector('[data-preview-code]');
      if (code) code.textContent = button.dataset.code;
      markSelectionChanged();
    }));

    visualizer.querySelectorAll('[data-material]').forEach(button => button.addEventListener('click', () => {
      visualizer.querySelectorAll('[data-material]').forEach(item => item.classList.remove('selected'));
      button.classList.add('selected');
      const output = visualizer.querySelector('[data-preview-material]');
      if (output) output.textContent = button.dataset.material;
      if (preview) preview.dataset.material = button.dataset.material.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');
      markSelectionChanged();
    }));

    upload?.addEventListener('change', async event => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;
      if (uploadTitle) uploadTitle.textContent = file.name;
      if (previewNote) previewNote.textContent = 'Preparing your wall photo…';
      try {
        wallImageData = await resizeWallPhoto(file);
        if (photo) photo.src = wallImageData;
        preview?.classList.add('has-photo');
        if (aiButton) aiButton.disabled = false;
        updateRequestLink();
        await generateAI();
      } catch {
        if (previewNote) previewNote.textContent = 'This image could not be opened. Please try a JPG, PNG or WebP file.';
      }
    });

    aiButton?.addEventListener('click', generateAI);
    updateRequestLink();
  }

  const requestParams = new URLSearchParams(location.search);
  const requestedProduct = requestParams.get('product');
  const contactBody = document.querySelector('[data-contact-body]');
  if (requestedProduct && contactBody && !contactBody.value) {
    contactBody.value = `I would like a quote for: ${requestedProduct}.\n\nProject dimensions and requirements: `;
  }
  const requestedPattern = requestParams.get('pattern');
  if (requestedPattern && contactBody && !contactBody.value) {
    const requestedDesign = requestParams.get('design');
    const requestedMaterial = requestParams.get('material');
    const requestedPreview = requestParams.get('preview');
    const previewLabel = requestedPreview === 'gemini' ? 'Generated with Gemini' : requestedPreview === 'openai' ? 'Generated with OpenAI' : requestedPreview === 'ai' ? 'AI generated' : requestedPreview === 'demo' ? 'Local concept preview' : 'Not generated';
    contactBody.value = `I would like a quote for the CNC visualiser concept.\n\nPattern: ${requestedPattern}${requestedDesign ? ` — ${requestedDesign}` : ''}\nMaterial: ${requestedMaterial || 'Not selected'}\nWall photo prepared: ${requestParams.get('wall') === 'uploaded' ? 'Yes — I will attach it to this enquiry' : 'No'}\nPreview: ${previewLabel}\n\nProject dimensions and requirements: `;
  }
});
